// packages/storefront/src/api/sendContactRequest.ts
import type { Result } from '@tourtask/types/result';
import type { AppLocale } from '@tourtask/types/i18n';
import type { ContactRequestProtection } from '@tourtask/types/contactRequest';
import { publicApiRequest } from './publicApiRequest';

export interface SendContactRequestProps {
	bizSlug: string;
	bizToken: string;
	protection: ContactRequestProtection;
	customer: {
		name: string;
		email: string;
		phone: { countryCode: string; number: string } | null;
		/** Optional: the storefront contact dialog sends one; the chat widget's
		 *  email-only fallback form does not. */
		subject?: string;
		message: string;
		locale: AppLocale;
	};
}

/**
 * `POST /v1/businesses/{bizSlug}/contact-requests`
 *
 * Generic "contact us" inquiry — fired from the contact button in the
 * embed not-embedded header. Distinct from `sendSpaceRequest`,
 * which is tied to a specific tour + date when the customer hits low
 * availability.
 *
 * Server delivers the message to every business contact email and
 * sends a branded confirmation back to the customer. The customer is
 * on the business-side `Reply-To` so staff can answer directly.
 */
export function sendContactRequest({
	bizSlug,
	bizToken,
	customer,
	protection,
}: SendContactRequestProps): Promise<Result<Record<string, never>>> {
	return publicApiRequest<Record<string, never>>({
		path: `businesses/${encodeURIComponent(bizSlug)}/contact-requests`,
		method: 'POST',
		body: { customer, protection },
		bizToken,
	});
}
